import { type PropsWithChildren, createContext, useContext, useEffect, useState } from 'react'

import { getLocationFromSearchQuery } from '@/domain/location/getLocationFromSearchQuery'
import { useGetSearchResultsData } from '@/hooks/srp/useGetSearchResultsData'
import {
  type SearchPageProps,
  type SearchPageServerProps,
} from '@/pages/srp/[...categoryOrLocation]'
import { setGlobalLocation } from '@/utils/location/location'

export type SrpPageProviderProps = PropsWithChildren<{
  searchPageProps: SearchPageProps & {
    pageLoadId: SearchPageServerProps['pageLoadId']
  }
}>

export const SrpPageContext = createContext<SearchPageProps>({
  pageLoadId: 'default',
} as SearchPageProps)

// SSR Props only, no manual updates
export const SrpPageProvider = ({ searchPageProps, children }: SrpPageProviderProps) => {
  const { data: { searchQuery } = {} } = useGetSearchResultsData()
  const [searchPagePropsCopy, setSearchPagePropsCopy] = useState(searchPageProps)

  const location = getLocationFromSearchQuery(searchQuery)

  // In NextJS when you navigate to the same page you are on, page state is preserved but
  // you get new props from getServerSideProps. This useEffect notifies any components
  // that new props have been received.
  useEffect(() => {
    // TODO: Temporary until we can deal with the fallback SRP Props on server error
    if (searchPageProps.errors?.hasServerError) {
      return
    }

    setSearchPagePropsCopy(searchPageProps)

    /** Set global category with current SRP category */

    /** Set global location with current SRP location */
    if (location) setGlobalLocation(location)
    // This should only ever run on page load when we get new props from getServerSideProps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery])

  return <SrpPageContext.Provider value={searchPagePropsCopy}>{children}</SrpPageContext.Provider>
}

export function useSrpContext() {
  return useContext(SrpPageContext)
}
